// Pure helpers for the id-based filter autocompletes (users screen).
// No React / Material-UI imports here so the logic stays unit-testable.

export const HISTORY_LIMIT = 5;
export const OPTIONS_LIMIT = 20;

export function historyStorageKey(field) {
  return `users-filter-history:${field}`;
}

const isPositiveInteger = value => Number.isInteger(value) && value > 0;

export function readHistory(storage, field) {
  try {
    const parsed = JSON.parse(storage.getItem(historyStorageKey(field)));

    return Array.isArray(parsed) && parsed.every(isPositiveInteger)
      ? parsed
      : [];
  } catch (e) {
    // Corrupted JSON or unavailable storage (private mode, blocked site data):
    // the history is a convenience, so it degrades to "no recent options".
    return [];
  }
}

export function pushHistory(storage, field, id) {
  const selectedId = Number(id);
  const history = [
    selectedId,
    ...readHistory(storage, field).filter(storedId => storedId !== selectedId),
  ].slice(0, HISTORY_LIMIT);

  try {
    storage.setItem(historyStorageKey(field), JSON.stringify(history));
  } catch (e) {
    // Quota exceeded or unavailable storage: skip saving, the filter still works.
  }
}

// Ids come as strings from the query string and as numbers from a selection.
export function resolveOption(options, id) {
  return options.find(option => String(option.id) === String(id)) || null;
}

export function companyOptionsFor(companies, buildingId) {
  if (!buildingId) {
    return companies;
  }

  return companies.filter(
    company => String(company.building_id) === String(buildingId)
  );
}

export function companyLabel(company, buildingNameById, hasBuildingSelected) {
  const buildingName = buildingNameById.get(company.building_id);

  if (hasBuildingSelected || !buildingName) {
    return company.name;
  }

  return `${company.name} — ${buildingName}`;
}

export function splitRecent(options, historyIds) {
  const recent = historyIds
    .map(id => resolveOption(options, id))
    .filter(Boolean)
    .map(option => ({ ...option, recent: true }));
  const recentIds = new Set(recent.map(option => String(option.id)));
  const others = options.filter(option => !recentIds.has(String(option.id)));

  return { recent, others };
}

// Builds the `filterOptions` prop: with an empty input the recent options come
// first (flagged clones, so they can be grouped); with text, the default
// accent/case-insensitive filter runs over the plain options.
export function buildFilterOptions(defaultFilter, historyIds) {
  return (options, state) => {
    if (state.inputValue !== '') {
      return defaultFilter(options, state);
    }

    const { recent, others } = splitRecent(options, historyIds);

    return [...recent, ...others].slice(0, OPTIONS_LIMIT);
  };
}
